import classNames from 'classnames';
import React, {useState} from 'react';

import Tooltip, {TooltipPlacement} from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import {MenuWidth} from 'ui-library/menus/MenuConstants';
import Option, {OptionProps} from 'ui-library/menus/Option';
import Section from 'ui-library/menus/Section';

export type ExportDropdownItem = Omit<OptionProps<string>, 'shouldEllipsize'>;

interface ExportDropdownProps {
  buttonStyle?: ButtonStyle;
  disabled?: boolean;
  menuItems: Array<Array<ExportDropdownItem>>;
  tooltipTitleWhenDisabled?: string | null;
}

export default function ExportDropdown({
  buttonStyle = ButtonStyle.TERTIARY_WHITE,
  disabled = false,
  menuItems,
  tooltipTitleWhenDisabled,
}: ExportDropdownProps): JSX.Element | null {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <Tooltip
      container="body"
      placement={TooltipPlacement.TOP}
      title={disabled ? tooltipTitleWhenDisabled : 'Export'}
      disabled={disabled && tooltipTitleWhenDisabled == null}
    >
      <div className={classNames('buttonGroup', {open: isOpen})}>
        <Dropdown
          isOpen={isOpen}
          onToggle={(newIsOpen) => {
            setIsOpen(newIsOpen);
          }}
          menu={
            <Menu dropLeft={true} width={MenuWidth.FIT_CONTENT}>
              {menuItems.map((section, sectionIndex) => (
                <Section key={sectionIndex}>
                  {section.map(({children, ...restOptionProps}, itemIndex) => (
                    <Option {...restOptionProps} key={itemIndex}>
                      {children}
                    </Option>
                  ))}
                </Section>
              ))}
            </Menu>
          }
        >
          <Button
            aria-label="Export"
            buttonStyle={buttonStyle}
            // The button used to receive focus when the dropdown was open, but now the dropdown itself
            // correctly receives focus. Pass `is-active` when open in order to preserve the previous visual
            // appearance of the button border
            className={isOpen ? 'is-active' : undefined}
            disabled={disabled}
            icon="export"
            isDropdown={true}
            size={ButtonSize.SMALL}
            dataTestAttrs={{component: 'ExportDropdown', element: 'button'}}
          />
        </Dropdown>
      </div>
    </Tooltip>
  );
}
